import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'context/AuthContext';
import { IniciativaFormatada } from 'types/iniciativa';
import {
  AvaliacaoEtapa2,
  AvaliacaoEtapa2Input,
  AvaliacaoIniciativa,
  AvaliacoesMap,
  CriterioKey,
  NotasCriterios,
  calcularTotalNotas,
  isAvaliacaoCompleta,
} from 'types/etapa2';
import {
  fetchAvaliacaoAtivaEtapa2,
  invalidarCacheAvaliacoesEtapa2,
  submitAvaliacaoEtapa2,
} from 'services/avaliacao-etapa2.service';
import { buscarIniciativasEtapa2 } from 'services/iniciativa.service';
import {
  buscarRascunho,
  deletarRascunho,
  salvarRascunho,
} from 'services/rascunho-etapa2.service';

const RASCUNHO_PREFIX = 'votacao-etapa-2-rascunho';
const rascunhoKey = (userId: string, category: string) =>
  `${RASCUNHO_PREFIX}:${userId}:${category}`;

interface UseAvaliacaoEtapa2 {
  iniciativas: IniciativaFormatada[];
  loadingIniciativas: boolean;
  erroIniciativas: string | null;

  avaliacoes: AvaliacoesMap;
  setNota: (iniciativaId: string, criterio: CriterioKey, valor: number) => void;
  removerAvaliacao: (iniciativaId: string) => void;

  podeEnviar: boolean;
  totalAvaliadas: number;
  totalIniciativas: number;

  enviando: boolean;
  enviar: () => Promise<void>;
  erroEnvio: string | null;

  avaliacaoAtiva: AvaliacaoEtapa2 | null;
  isReadOnly: boolean;
  editMode: boolean;
  iniciarEdicao: () => void;
  cancelarEdicao: () => void;
}

export function useAvaliacaoEtapa2(category: string | null): UseAvaliacaoEtapa2 {
  const { user } = useAuth();
  const [iniciativas, setIniciativas] = useState<IniciativaFormatada[]>([]);
  const [loadingIniciativas, setLoadingIniciativas] = useState(false);
  const [erroIniciativas, setErroIniciativas] = useState<string | null>(null);
  const [avaliacoes, setAvaliacoes] = useState<AvaliacoesMap>({});
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [avaliacaoAtiva, setAvaliacaoAtiva] = useState<AvaliacaoEtapa2 | null>(null);
  const [editMode, setEditMode] = useState(false);

  // Refs para leitura em callbacks sem adicionar às dependências do useCallback
  const isReadOnlyRef = useRef(false);
  const avaliacaoAtivaRef = useRef<AvaliacaoEtapa2 | null>(null);
  // Sinaliza que a carga inicial (remoto/rascunho) já completou para a
  // combinação atual de category/userId. Enquanto false, o effect que
  // persiste rascunho não escreve (evita wipe do localStorage no mount com
  // avaliacoes={} inicial).
  const loadDoneRef = useRef(false);
  const avaliacoesRef = useRef<AvaliacoesMap>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasUnsyncedChangesRef = useRef(false);

  // Carrega iniciativas + hidrata avaliação ativa remota (tem prioridade) ou rascunho local
  useEffect(() => {
    loadDoneRef.current = false;

    if (!category) {
      setIniciativas([]);
      setAvaliacoes({});
      setAvaliacaoAtiva(null);
      avaliacaoAtivaRef.current = null;
      setEditMode(false);
      isReadOnlyRef.current = false;
      return;
    }

    let canceled = false;

    const load = async () => {
      setLoadingIniciativas(true);
      setErroIniciativas(null);
      try {
        const [inis, ativa, rascunhoRemoto] = await Promise.all([
          buscarIniciativasEtapa2(category, user?._id),
          user?._id
            ? fetchAvaliacaoAtivaEtapa2(user._id, category)
            : Promise.resolve(null),
          user?._id
            ? buscarRascunho(user._id, category)
            : Promise.resolve(null),
        ]);
        if (canceled) return;

        setIniciativas(inis);
        setEditMode(false);

        if (ativa) {
          // Avaliação remota ativa: hidratar com notas remotas e limpar rascunho local
          setAvaliacaoAtiva(ativa);
          avaliacaoAtivaRef.current = ativa;
          isReadOnlyRef.current = true;

          const inicial: AvaliacoesMap = {};
          ativa.avaliacoes.forEach((av: AvaliacaoIniciativa) => {
            inicial[av.iniciativaId] = av.notas;
          });
          setAvaliacoes(inicial);

          // Limpar rascunho residual da categoria (não deve sobrescrever o remoto)
          if (user?._id) {
            try {
              window.localStorage.removeItem(rascunhoKey(user._id, category));
            } catch {
              /* ignora */
            }
          }
        } else {
          // Sem avaliação remota: rascunho remoto tem prioridade; fallback para localStorage
          setAvaliacaoAtiva(null);
          avaliacaoAtivaRef.current = null;
          isReadOnlyRef.current = false;

          let inicial: AvaliacoesMap = {};
          if (rascunhoRemoto) {
            inicial = rascunhoRemoto;
          } else if (user?._id) {
            try {
              const raw = window.localStorage.getItem(
                rascunhoKey(user._id, category)
              );
              if (raw) inicial = JSON.parse(raw) as AvaliacoesMap;
            } catch {
              /* ignora */
            }
          }
          setAvaliacoes(inicial);
        }

        loadDoneRef.current = true;
      } catch (err: unknown) {
        if (canceled) return;
        setErroIniciativas(
          err instanceof Error ? err.message : 'Erro ao carregar iniciativas'
        );
        setIniciativas([]);
      } finally {
        if (!canceled) setLoadingIniciativas(false);
      }
    };

    load();
    return () => {
      canceled = true;
    };
  }, [category, user?._id]);

  // isReadOnly derivado do estado atual
  const isReadOnly = !!avaliacaoAtiva && !editMode;

  // Mantém refs sincronizadas para uso nos callbacks
  isReadOnlyRef.current = isReadOnly;
  avaliacaoAtivaRef.current = avaliacaoAtiva;

  // Persiste rascunho no localStorage somente quando não estiver em modo read-only.
  // O guard de loadDoneRef evita que o estado inicial vazio sobrescreva o
  // rascunho ANTES da carga assíncrona do localStorage completar.
  // Após persistir localmente, dispara um debounce de 4s para sincronizar
  // com a collection remota do Funifier.
  useEffect(() => {
    if (!category) return;
    if (!user?._id) return;
    if (isReadOnly) return;
    if (!loadDoneRef.current) return;

    avaliacoesRef.current = avaliacoes;

    try {
      window.localStorage.setItem(
        rascunhoKey(user._id, category),
        JSON.stringify(avaliacoes)
      );
    } catch {
      /* ignora quota / disabled */
    }

    hasUnsyncedChangesRef.current = true;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const uid = user._id;
    const cat = category;
    debounceRef.current = setTimeout(() => {
      salvarRascunho(uid, cat, avaliacoesRef.current).catch(console.error);
      hasUnsyncedChangesRef.current = false;
      debounceRef.current = null;
    }, 4_000);
  }, [avaliacoes, category, isReadOnly, user?._id]);

  // Sync periódico a cada 30s: garante que mudanças não enviadas pelo debounce
  // (ex: usuário digitando continuamente) cheguem ao servidor.
  useEffect(() => {
    if (!category || !user?._id) return;
    const uid = user._id;
    const cat = category;

    intervalRef.current = setInterval(() => {
      if (
        hasUnsyncedChangesRef.current &&
        !isReadOnlyRef.current &&
        loadDoneRef.current
      ) {
        salvarRascunho(uid, cat, avaliacoesRef.current).catch(console.error);
        hasUnsyncedChangesRef.current = false;
      }
    }, 30_000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [category, user?._id]);

  // Sincroniza rascunho imediatamente antes de o usuário sair da página,
  // sem aguardar o debounce de 4s.
  useEffect(() => {
    if (!category || !user?._id) return;
    const uid = user._id;
    const cat = category;

    const handleBeforeUnload = () => {
      if (hasUnsyncedChangesRef.current && !isReadOnlyRef.current) {
        salvarRascunho(uid, cat, avaliacoesRef.current);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [category, user?._id]);

  const setNota = useCallback(
    (iniciativaId: string, criterio: CriterioKey, valor: number) => {
      // Defesa em profundidade: no-op se read-only
      if (isReadOnlyRef.current) return;
      setAvaliacoes((prev) => ({
        ...prev,
        [iniciativaId]: {
          ...(prev[iniciativaId] ?? {}),
          [criterio]: valor,
        },
      }));
    },
    []
  );

  const removerAvaliacao = useCallback(
    (iniciativaId: string) => {
      // Defesa em profundidade: no-op se read-only
      if (isReadOnlyRef.current) return;
      setAvaliacoes((prev) => {
        const next = { ...prev };
        delete next[iniciativaId];
        return next;
      });
    },
    []
  );

  const iniciarEdicao = useCallback(() => {
    setEditMode(true);
    isReadOnlyRef.current = false;
  }, []);

  const cancelarEdicao = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    hasUnsyncedChangesRef.current = false;
    setEditMode(false);
    isReadOnlyRef.current = true;
    const currentAtiva = avaliacaoAtivaRef.current;
    if (currentAtiva) {
      const inicial: AvaliacoesMap = {};
      currentAtiva.avaliacoes.forEach((av) => {
        inicial[av.iniciativaId] = av.notas;
      });
      setAvaliacoes(inicial);
    }
    if (category && user?._id) {
      try {
        window.localStorage.removeItem(rascunhoKey(user._id, category));
      } catch {
        /* ignora */
      }
      deletarRascunho(user._id, category).catch(console.error);
    }
  }, [category, user?._id]);

  const totalIniciativas = iniciativas.length;
  const totalAvaliadas = useMemo(
    () =>
      iniciativas.filter((ini) => isAvaliacaoCompleta(avaliacoes[ini.id]))
        .length,
    [iniciativas, avaliacoes]
  );
  const podeEnviar =
    totalIniciativas > 0 && totalAvaliadas === totalIniciativas;

  const enviar = useCallback(async () => {
    if (enviando) return;
    if (!user || !category || !podeEnviar) return;
    setEnviando(true);
    setErroEnvio(null);
    try {
      const avaliacoesPayload: AvaliacaoIniciativa[] = iniciativas.map((ini) => {
        const notas = avaliacoes[ini.id] as NotasCriterios;
        return {
          iniciativaId: String(ini.id),
          iniciativaTitulo: String(ini.titulo ?? ''),
          notas: { ...notas },
          total: calcularTotalNotas(notas),
        };
      });

      const matricula = String(
        user.detail?.acessor?.vp?.matricula ?? user.detail?.matricula ?? ''
      );

      const payload: AvaliacaoEtapa2Input = {
        category,
        avaliadorId: user._id,
        avaliadorName: String(user.name ?? ''),
        avaliadorMatricula: matricula,
        enviadoEm: Date.now(),
        avaliacoes: avaliacoesPayload,
        totalIniciativas: avaliacoesPayload.length,
      };

      const novaAvaliacao = await submitAvaliacaoEtapa2(payload);

      try {
        window.localStorage.removeItem(rascunhoKey(user._id, category));
      } catch {
        /* ignora */
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      hasUnsyncedChangesRef.current = false;
      deletarRascunho(user._id, category).catch(console.error);

      invalidarCacheAvaliacoesEtapa2(user._id, category);

      // Atualiza avaliacaoAtiva com o novo doc e sai do modo edit
      setAvaliacaoAtiva(novaAvaliacao);
      avaliacaoAtivaRef.current = novaAvaliacao;
      setEditMode(false);
      isReadOnlyRef.current = true;
    } catch (err: unknown) {
      const mensagem = err instanceof Error ? err.message : 'Erro ao enviar avaliação';
      setErroEnvio(mensagem);
      throw err;
    } finally {
      setEnviando(false);
    }
  }, [enviando, user, category, podeEnviar, iniciativas, avaliacoes]);

  return {
    iniciativas,
    loadingIniciativas,
    erroIniciativas,
    avaliacoes,
    setNota,
    removerAvaliacao,
    podeEnviar,
    totalAvaliadas,
    totalIniciativas,
    enviando,
    enviar,
    erroEnvio,
    avaliacaoAtiva,
    isReadOnly,
    editMode,
    iniciarEdicao,
    cancelarEdicao,
  };
}
